import { useState, useEffect, useRef, useMemo, type JSX } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import clsx from 'clsx';

/**
 * TierConfigModal — Day 58 — quantity-tier configuration for tier-mode
 * Buying Session analysis.
 *
 * Sibling-backdrop modal pattern (matches the Save Preset / Save as Draft
 * modals in BuyingSession.tsx). Width 520px to fit qty_min / qty_max /
 * unit_cost / trash row layout cleanly.
 *
 * On Save, emits the validated tier array up to the parent which patch-merges
 * it into ui_state.tier_mode via the existing useBuyingSession.updateUiState
 * path.
 *
 * Validation (per row, inline below the offending row):
 *   - qty_min must be > previous row's qty_max (or 1 on the first row)
 *   - qty_max must be ≥ qty_min in the same row
 *   - qty_max may only be null on the LAST row
 *   - unit_cost > 0
 *   - Save disabled while any error is active
 *
 * N >= 5 advisory: not blocking, but the modal surfaces an inline notice that
 * the resulting grid columns may exceed the Architecture Rule 16 width budget
 * (~1180px) at 5+ tiers (2 cols per tier × N tiers × 88px each).
 */

interface TierRow {
  qty_min: number;
  qty_max: number | null;
  unit_cost: number;
}

interface TierConfigModalProps {
  open: boolean;
  tiers: TierRow[];
  onSave: (tiers: TierRow[]) => void;
  onClose: () => void;
}

import { validateDrafts, type DraftRow } from './tierValidation';

const DEFAULT_DRAFT: DraftRow[] = [
  { qty_min: '1', qty_max: '99', unit_cost: '' },
  { qty_min: '100', qty_max: '499', unit_cost: '' },
  { qty_min: '500', qty_max: '', unit_cost: '' },
];

function rowToDraft(row: TierRow): DraftRow {
  return {
    qty_min: String(row.qty_min),
    qty_max: row.qty_max == null ? '' : String(row.qty_max),
    unit_cost: String(row.unit_cost),
  };
}

function draftToRow(d: DraftRow): TierRow {
  return {
    qty_min: Number(d.qty_min),
    qty_max: d.qty_max.trim() === '' ? null : Number(d.qty_max),
    unit_cost: Number(d.unit_cost),
  };
}

// Day 79 P5.4 — validateDrafts + DraftRow extracted to ./tierValidation
// (preserves Day 66 baseline 0 errors / 3 warnings; export-mixed-with-
// component would otherwise trip react-refresh/only-export-components).

export default function TierConfigModal({
  open,
  tiers,
  onSave,
  onClose,
}: TierConfigModalProps): JSX.Element | null {
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  // Seed local state on open. Pre-existing tiers hydrate; empty array seeds
  // the default 3-tier scaffold so the buyer can edit unit costs immediately.
  useEffect(() => {
    if (!open) return;
    if (tiers.length === 0) {
      setDrafts(DEFAULT_DRAFT.map((d) => ({ ...d })));
    } else {
      setDrafts(tiers.map(rowToDraft));
    }
  }, [open, tiers]);

  // Programmatic focus on first input when modal opens.
  useEffect(() => {
    if (!open) return;
    setTimeout(() => firstInputRef.current?.focus(), 0);
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const errors = useMemo(() => validateDrafts(drafts), [drafts]);
  const hasErrors = errors.some((e) => e !== null);

  const updateRow = (idx: number, patch: Partial<DraftRow>): void => {
    setDrafts((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx: number): void => {
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
  };

  const addRow = (): void => {
    setDrafts((prev) => {
      const last = prev[prev.length - 1];
      const lastMax = last?.qty_max?.trim() ? Number(last.qty_max) : null;
      const nextMin = lastMax != null ? lastMax + 1 : '';
      return [...prev, { qty_min: String(nextMin), qty_max: '', unit_cost: '' }];
    });
  };

  const handleSave = (): void => {
    if (hasErrors || drafts.length === 0) return;
    onSave(drafts.map(draftToRow));
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Configure tier pricing"
      data-testid="tier-config-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        tabIndex={-1}
        className="absolute inset-0 bg-black/60 cursor-default"
      />
      <div className="relative bg-surface-2 border border-border rounded-xl shadow-2xl w-[520px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-subtle">
          <h2 className="text-sm font-semibold text-ink-bright">Configure Pricing Tiers</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="text-ink-muted hover:text-ink-primary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <p className="text-xs text-ink-secondary leading-relaxed">
            Set quantity tiers and per-unit cost. Leave the last tier&apos;s max blank for &quot;and
            above.&quot; Tiers must be contiguous (no overlap or gap).
          </p>

          {/* Header row */}
          <div className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 text-2xs uppercase tracking-wider text-ink-muted px-1">
            <span>Qty min</span>
            <span>Qty max</span>
            <span>Unit cost</span>
            <span></span>
          </div>

          {/* Tier rows */}
          <div className="space-y-1.5">
            {drafts.map((row, idx) => {
              const err = errors[idx];
              return (
                <div key={idx} data-testid={`tier-row-${idx}`}>
                  <div className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 items-center">
                    <input
                      ref={idx === 0 ? firstInputRef : undefined}
                      type="text"
                      inputMode="numeric"
                      value={row.qty_min}
                      onChange={(e) => updateRow(idx, { qty_min: e.target.value })}
                      placeholder="1"
                      data-testid={`tier-qty-min-${idx}`}
                      className="arbi-input text-xs font-mono text-right"
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={row.qty_max}
                      onChange={(e) => updateRow(idx, { qty_max: e.target.value })}
                      placeholder={idx === drafts.length - 1 ? '(blank = +)' : '99'}
                      data-testid={`tier-qty-max-${idx}`}
                      className="arbi-input text-xs font-mono text-right"
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      value={row.unit_cost}
                      onChange={(e) => updateRow(idx, { unit_cost: e.target.value })}
                      placeholder="0.00"
                      data-testid={`tier-unit-cost-${idx}`}
                      className="arbi-input text-xs font-mono text-right"
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      aria-label={`Remove tier ${idx + 1}`}
                      disabled={drafts.length <= 1}
                      data-testid={`tier-remove-${idx}`}
                      className={clsx(
                        'flex items-center justify-center text-ink-muted transition-colors',
                        drafts.length <= 1 ? 'opacity-30 cursor-not-allowed' : 'hover:text-danger'
                      )}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {err ? <p className="text-2xs text-danger mt-0.5 px-1">{err}</p> : null}
                </div>
              );
            })}
          </div>

          {/* Add Tier */}
          <button
            type="button"
            onClick={addRow}
            data-testid="tier-add-row"
            className="arbi-btn-ghost text-2xs flex items-center gap-1 px-2 py-1"
          >
            <Plus size={12} /> Add Tier
          </button>

          {/* N >= 5 advisory */}
          {drafts.length >= 5 ? (
            <div className="bg-warning/10 border border-warning/30 rounded text-2xs text-warning px-2 py-1.5">
              Tier comparison columns may exceed grid width budget at 5+ tiers (2 cols per tier ×{' '}
              {drafts.length} tiers ≈ {drafts.length * 176}px). Save still allowed.
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border-subtle">
          <button
            type="button"
            onClick={onClose}
            data-testid="tier-config-cancel"
            className="arbi-btn-ghost text-xs px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={hasErrors || drafts.length === 0}
            data-testid="tier-config-save"
            className="arbi-btn-primary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Tiers
          </button>
        </div>
      </div>
    </div>
  );
}
